import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { subscribeCollectionAdded } from '../../lib/collectionEvents'
import { loadBottleDetails, saveBottleAnswers } from '../../lib/bottleFormat'
import { supabase } from '../../lib/supabase'
import { Modal } from '../ui'
import BottleOrigin from './BottleOrigin'
import { useAuth } from '../../App'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { saveGiftMemory } from '../../lib/memoryCaptureBridge'

export default function PostAddOrigin({ client = supabase }) {
  const { user } = useAuth()
  const captureBridgeEnabled = useFeatureFlag('memory_capture_bridge')
  const [queue, setQueue] = useState([])
  const [memoryNotice, setMemoryNotice] = useState(null)
  const [otherDialog, setOtherDialog] = useState(false)
  const seen = useRef(new Set())
  useEffect(() => subscribeCollectionAdded(rows => {
    const fresh = (rows || []).filter(row => row.id && !seen.current.has(row.id))
    fresh.forEach(row => seen.current.add(row.id))
    setQueue(current => [...current, ...fresh])
  }), [])
  useEffect(() => {
    const check = () => setOtherDialog([...document.querySelectorAll('[role="dialog"]')].some(dialog => !dialog.querySelector('[data-origin-prompt]')))
    check()
    const observer = new MutationObserver(check)
    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])
  const next = () => setQueue(current => current.slice(1))
  const row = queue[0]
  // The exact size lives on the bottle row (a 1.5 ml sample reads 2 on the collection row),
  // so read it before asking. If it cannot be read, ask with the collection row's size.
  const [details, setDetails] = useState(null)
  useEffect(() => {
    if (!row?.id) { setDetails(null); return undefined }
    let active = true
    setDetails(null)
    loadBottleDetails(client, row.id, row.bottle_ml ?? null)
      .catch(() => ({ bottle_format: null, size_ml: row.bottle_ml ?? null, on_hand: 1, bottle_id: null }))
      .then(result => { if (active) setDetails({ id: row.id, ...result }) })
    return () => { active = false }
  }, [client, row?.id, row?.bottle_ml])
  const ready = Boolean(row) && details?.id === row.id
// Full screen on phones (like the sheets fixed in PR #548) so the keyboard can never cover
  // the store name field or Save (FOS-SOURCE-PICKER-KEYBOARD-001, Rob 2026-09-30).
  return <>
  {memoryNotice && <div role="alert" data-gift-memory-failed className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-sm rounded-xl border border-gold/40 bg-charcoal-800 p-3 text-sm text-cream shadow-lg">
    <p>Saved your bottle, but “Received from {memoryNotice.name}” did not reach your Memory Log.</p>
    <div className="mt-2 flex justify-end gap-3">
      <button type="button" className="min-h-11 px-2 text-cream/60" onClick={() => setMemoryNotice(null)}>Dismiss</button>
      <Link to={`/fragrance/${memoryNotice.fragranceId}/log`} state={{ addMemory: 'received' }} onClick={() => setMemoryNotice(null)} className="min-h-11 rounded-lg border border-gold/60 px-3 py-2.5 text-gold">Add it now</Link>
    </div>
  </div>}
  <Modal open={ready && !otherDialog} onClose={next} title="Bottle added" maxWidth="max-w-sm" fullScreenMobile bodyClassName="overflow-x-hidden scroll-pb-24">
    {ready && <div data-origin-prompt className="min-w-0 pb-24 sm:pb-1"><p className="mb-3 text-sm text-cream/70">{row.name || 'Your new bottle'}</p>
      <BottleOrigin key={row.id} postAdd offerGiftMemory={captureBridgeEnabled} initial={details} onSkip={next} onSave={async values => {
        const saved = await saveBottleAnswers(client, row.id, values)
        // The bottle and its origin are saved; a failed memory write must not undo or block
        // that, but it must not vanish either: say so, with a one-tap way to add it by hand.
        if (captureBridgeEnabled) {
          await saveGiftMemory(client, { userId: user?.id, fragrance: row, values })
            .catch(() => setMemoryNotice({ fragranceId: row.id, name: (values.acquired_from_name || '').trim() }))
        }
        next()
        return saved
      }} />
    </div>}
  </Modal>
  </>
}
